import { publishWorkflowAction } from './app-state/crossFeatureWorkflowExternalState.js';
import { buildOidcSessionSource, describeSessionState, sessionIdentityKey } from './auth/oidcIdentity.js';
import {
  loadOidcUser, onOidcUserChange, recordSessionRejection, renewOidcUser, resetSessionRejections,
  resetSignInRedirectGuard, signOutRedirect, startSignInRedirect,
} from './auth/oidcClient.js';
import { createRenewalCoordinator } from './auth/renewalCoordinator.js';
import { clearOrganizationScope, onOrganizationScopeChange, organizationScopeFor } from './auth/organizationScope.js';
import { canRoleAccessModule, getPermissionsForRole, USER_ROLES } from './rolePolicy.js';
import { normalizeUserIdentity } from './userIdentity.js';
const AUTH_CONFIG_WINDOW_KEYS = ['__MITUS_AUTH__', '__MITUS_RUNTIME_CONFIG__', '__MITUS_FINANCIAL_CONFIG__'];
const LOCAL_API_PORT = process.env.REACT_APP_MITUS_API_PORT;
const KNOWN_ROLES = Object.values(USER_ROLES);
export const AUTH_EVENT_NAME = 'mitus-auth-state-changed';
let accessSession = null;
// Logical login identity: token renewal does not create a new login generation.
let accessSessionGeneration = 0;
let accessSessionKey = '';
let oidcEventsBound = false;
// Set when the API kept rejecting fresh sessions: automatic sign-in stops until "Sign in again".
let signInLoopDetected = false;
function applyOidcUser(user) {
  const source = buildOidcSessionSource(user, KNOWN_ROLES);
  const key = source ? sessionIdentityKey(source) : '';
  if (key !== accessSessionKey) {
    accessSessionGeneration += 1;
    accessSessionKey = key;
  }
  // A signed-out (or unloaded) tab drops any cached organization scope: a returning sign-in
  // (the same person or someone else) gets a fresh /me/access answer, not yesterday's.
  if (!source) clearOrganizationScope();
  accessSession = source;
  // A fresh token (from any renewal path) or a signed-out tab leaves nothing to retry.
  if (!source || !describeSessionState(source).accessTokenExpired) renewal.settled();
  dispatchAuthUpdate();
  return accessSession;
}
function accessTokenExpiresAtMs(source) {
  return Date.parse(source?.sessionExpiresAt || '');
}
// Every renewal path (automatic renewal, expiry, restore, API 401) goes through one
// single-flight refresh; only a definitive Keycloak refusal ends the session.
const renewal = createRenewalCoordinator({
  renew: async () => applyOidcUser(await renewOidcUser()),
  readSession: () => accessSession && {
    generation: accessSessionGeneration,
    accessToken: accessSession.accessToken,
    expiresAtMs: accessTokenExpiresAtMs(accessSession),
    renewable: accessSession.renewable,
  },
  endSession: () => applyOidcUser(null),
});
function bindOidcEvents() {
  if (oidcEventsBound) return;
  oidcEventsBound = true;
  onOidcUserChange({
    onLoaded: applyOidcUser,
    onUnloaded: () => applyOidcUser(null),
    onAccessTokenExpired: () => { void renewal.accessTokenExpired(); },
    onRenewError: (error) => { renewal.renewalFailed(error); },
  });
}
export async function restoreAccessSession() {
  bindOidcEvents();
  let session;
  try {
    session = applyOidcUser(await loadOidcUser());
  } catch (error) {
    applyOidcUser(null);
    throw error;
  }
  // A stored token that expired (reload after sleep) is renewed before the workspace mounts;
  // a transient failure keeps the session and retries in the background.
  if (session && describeSessionState(session).accessTokenExpired) await renewal.renew().catch(() => null);
  return accessSession;
}
export function beginSignIn(onFailure) {
  if (signInLoopDetected) return false;
  return startSignInRedirect({ onFailure });
}
export function retrySignIn(onFailure) {
  signInLoopDetected = false;
  resetSignInRedirectGuard();
  resetSessionRejections();
  return startSignInRedirect({ onFailure });
}
// Single-flight renewal; resolves with the renewed session. Failure handling (end the
// session only on a definitive Keycloak error, otherwise retry with backoff) is built in.
export function renewAccessSession() {
  return renewal.renew();
}
// The API answered 401 for a request sent with `rejectedProfile`. Resolves with the current
// session once a newer token is available (renewed now, or already by another path).
export function renewAccessSessionAfterRejection(rejectedProfile) {
  return renewal.renewAfterRejection({ generation: rejectedProfile?.sessionGeneration, accessToken: String(rejectedProfile?.accessToken || '') });
}
export function clearAccessSession() {
  applyOidcUser(null);
}
export async function endAccessSession() {
  applyOidcUser(null);
  await signOutRedirect();
}
function resolveApiBaseUrl() {
  const windowConfig = readWindowConfig();
  const env = readEnvConfig();
  const base = String(firstDefined(windowConfig.apiBaseUrl, windowConfig.financialApiBaseUrl, env.REACT_APP_MITUS_FINANCIAL_API_BASE_URL, env.REACT_APP_MITUS_FINANCIAL_BASE_URL, '') || '').replace(/\/$/, '');
  if (base) return base;
  if (isLocalBrowserHost()) {
    const isolatedPort = new URLSearchParams(window.location.search || '').get('apiPort');
    const configuredPort = String(isolatedPort || LOCAL_API_PORT || '4010').trim();
    if (!/^\d{1,5}$/.test(configuredPort) || Number(configuredPort) < 1 || Number(configuredPort) > 65535) {
      throw new Error('REACT_APP_MITUS_API_PORT must be a valid TCP port.');
    }
    return `${window.location.protocol}//${window.location.hostname}:${configuredPort}`;
  }
  return '';
}
// Neutral consumers use the configured service authority.
// This does not select an alternate endpoint after a failed request.
export function resolveAuthenticatedApiUrl(pathname) {
  return `${resolveApiBaseUrl()}${pathname}`;
}
function readWindowConfig() {
  if (typeof window === 'undefined') return {};
  return AUTH_CONFIG_WINDOW_KEYS.reduce((result, key) => ({ ...result, ...(window[key] || {}) }), {});
}
function readEnvConfig() { return typeof process !== 'undefined' ? process.env || {} : {}; }
function firstDefined(...values) { return values.find(value => value !== undefined && value !== null && value !== ''); }
function isLocalBrowserHost() {
  return typeof window !== 'undefined' && ['localhost', '127.0.0.1'].includes(window.location.hostname);
}
function matchesPermission(granted, requested) {
  if (granted === '*' || granted === requested) return true;
  return granted.endsWith('.*') && (requested === granted.slice(0, -2) || requested.startsWith(granted.slice(0, -1)));
}
export function hasPermission(permission, profile = getRuntimeAccessProfile()) {
  return !!profile.sessionActive && (profile.permissions || []).some(granted => matchesPermission(granted, permission));
}
// `session` defaults to the module's own live session and is only ever overridden in tests
// (accessControl.test.js), which have no way to drive a real oidc-client-ts sign-in.
export function getRuntimeAccessProfile(session = accessSession) {
  const source = session || {};
  const identity = normalizeUserIdentity(source);
  // Admin Platform managers: their families come from /me/access (auth/organizationScope.js).
  const organizationScope = organizationScopeFor(identity.userId);
  if (organizationScope) identity.assignedFamilyIds = [...organizationScope];
  // An expired access token that a refresh token can renew keeps the session active;
  // the next API 401 (or the expiry event) renews it through the single-flight renewal.
  const { accessTokenExpired, renewable, sessionExpired, roleMissing, sessionActive: stateActive } = describeSessionState(session);
  const sessionActive = stateActive && !!identity.role;
  const permissions = sessionActive ? getPermissionsForRole(identity.role) : [];
  const authStatus = sessionExpired ? 'session-expired' : sessionActive ? 'authenticated' : 'anonymous';
  const customerScope = identity.capabilities.isFamilyMember ? [identity.familyId].filter(Boolean) : identity.assignedFamilyIds;
  return {
    ...identity,
    userName: identity.userDisplayName,
    sessionGeneration: accessSessionGeneration,
    authStatus, authStatusLabel: sessionActive ? 'Authenticated' : sessionExpired ? 'Session expired' : 'Anonymous',
    sessionStatusLabel: sessionActive ? 'Session active' : sessionExpired ? 'Session expired' : 'Anonymous',
    sessionActive, isAuthenticated: sessionActive, sessionExpired,
    accessTokenExpired, renewable,
    // A Keycloak user is signed in but has no Mitus role: show "No Mitus access", don't redirect.
    roleMissing,
    // Legacy consumers use this as an Administration capability, not role identity.
    isPlatformAdmin: sessionActive && identity.capabilities.fullWorkspace,
    isAdvisor: sessionActive && identity.role === 'advisor', isAdvisorFirm: false,
    permissions, permissionCount: permissions.length,
    permissionSummary: [{ family: 'module', label: 'Application access', count: permissions.length, permissions }],
    customerScope, customerScopeCount: customerScope.length,
    legalEntityScope: [], legalEntityScopeCount: 0,
    claims: [], claimCount: 0,
    tenantId: String(source.tenantId || ''),
    accessToken: String(source.accessToken || ''),
    sessionExpiresAt: source.sessionExpiresAt || null,
    authMode: source.authMode || 'anonymous', authModeLabel: source.authMode || 'anonymous',
    authSourceLabel: source.authSource || 'anonymous', accessSource: source.authSource || 'anonymous',
    backendEnforcementLabel: 'Application roles and customer scope are enforced by both the workspace and API.',
  };
}
export function canAccessModule(module, profile = getRuntimeAccessProfile()) {
  return !!profile.sessionActive && canRoleAccessModule(profile.role, module);
}
/** @template T @param {readonly T[]} modules @param {ReturnType<typeof getRuntimeAccessProfile>} profile */
export function filterModulesByAccess(modules = [], profile = getRuntimeAccessProfile()) {
  return modules.filter(module => canAccessModule(module, profile));
}
export function ensurePlatformAdminAccess(actionLabel = 'perform this action', profile = getRuntimeAccessProfile()) {
  if (!profile.sessionActive || !profile.capabilities.fullWorkspace) throw new Error(`Administrator or Advisor access is required to ${actionLabel}.`);
  return profile;
}
export function buildApiAuthHeaders(accessProfile = getRuntimeAccessProfile()) {
  const headers = {
    'X-GenTransition-Auth-Status': accessProfile.authStatus,
    'X-GenTransition-Role': accessProfile.role,
    'X-GenTransition-Session-Status': accessProfile.sessionStatusLabel,
    'X-GenTransition-Permissions': Array.isArray(accessProfile.permissions) ? accessProfile.permissions.join(',') : ''
  };
  if (accessProfile.userId) headers['X-GenTransition-User-Id'] = accessProfile.userId;
  if (accessProfile.userEmail) headers['X-GenTransition-User-Email'] = accessProfile.userEmail;
  if (accessProfile.tenantId) headers['X-GenTransition-Tenant-Id'] = accessProfile.tenantId;
  if (Array.isArray(accessProfile.customerScope) && accessProfile.customerScope.length) headers['X-GenTransition-Customer-Scope'] = accessProfile.customerScope.join(',');
  if (Array.isArray(accessProfile.legalEntityScope) && accessProfile.legalEntityScope.length) headers['X-GenTransition-Legal-Entity-Scope'] = accessProfile.legalEntityScope.join(',');
  if (accessProfile.sessionExpiresAt) headers['X-GenTransition-Session-Expires-At'] = accessProfile.sessionExpiresAt;
  if (accessProfile.accessToken) headers.Authorization = `Bearer ${accessProfile.accessToken}`;
  return headers;
}
const API_AUTH_FAILURE_STATUSES = new Set([401, 403, 419, 440]);
function isApiAuthorizationFailure(status) {
  return API_AUTH_FAILURE_STATUSES.has(Number(status));
}
function buildApiAuthorizationFailure(status, payload, accessProfile = getRuntimeAccessProfile()) {
  const numericStatus = Number(status);
  const statusLabel = Number.isFinite(numericStatus) ? numericStatus : 0;
  const serverMessage = payload?.message || payload?.error || payload?.detail || payload?.raw || '';
  if (numericStatus === 403) {
    return {
      status: statusLabel,
      code: 'api-access-forbidden',
      isAuthError: true,
      requiresSignIn: false,
      sessionExpired: false,
      message: serverMessage || `${(accessProfile?.roleLabel || 'Unknown role')} access does not permit this API action.`
    };
  }
  return {
    status: statusLabel || 401,
    code: accessProfile?.sessionExpired ? 'session-expired' : 'authentication-required',
    isAuthError: true,
    requiresSignIn: true,
    sessionExpired: accessProfile?.sessionExpired || numericStatus === 419 || numericStatus === 440,
    message: serverMessage || 'Your Gen Transition session is no longer active. Sign in again to continue.'
  };
}
export function handleApiAuthorizationFailure(status, payload, accessProfile = getRuntimeAccessProfile()) {
  if (!isApiAuthorizationFailure(status)) {
    return null;
  }
  const failure = buildApiAuthorizationFailure(status, payload, accessProfile);
  // A stale response from an older login is ignored.
  if (failure.requiresSignIn && accessSession && accessProfile?.sessionGeneration === accessSessionGeneration) settleRejectedSession(accessProfile);
  return failure;
}
// The API refused the token in `rejectedProfile`:
// - a newer, unexpired token is already stored: the request was stale, keep the session;
// - the rejected token had expired and a refresh token exists: renew (single-flight) and end
//   the session only if Keycloak definitively refuses;
// - otherwise the API refused a token we consider valid: clear the session, so AuthProvider
//   redirects to Keycloak (repeated rejections stop the automatic redirects).
function settleRejectedSession(rejectedProfile) {
  const rejectedToken = String(rejectedProfile?.accessToken || '');
  const current = describeSessionState(accessSession);
  if (accessSession.accessToken !== rejectedToken && !current.accessTokenExpired) return;
  const rejectedExpiry = Date.parse(rejectedProfile?.sessionExpiresAt || '');
  const rejectedTokenExpired = Boolean(rejectedProfile?.accessTokenExpired) || (Number.isFinite(rejectedExpiry) && rejectedExpiry <= Date.now());
  if (rejectedTokenExpired && current.renewable) {
    void renewAccessSessionAfterRejection(rejectedProfile).catch(() => null);
    return;
  }
  if (!recordSessionRejection()) signInLoopDetected = true;
  clearAccessSession();
}
function dispatchAuthUpdate() {
  publishWorkflowAction(AUTH_EVENT_NAME, {
    updatedAt: new Date().toISOString()
  });
}
onOrganizationScopeChange(() => dispatchAuthUpdate());
